// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ExternalLink from "@store/Library/Components/Toolkit/Icons/ExternalLink";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { css, keyframes } from "styled-components";
import GreifCarouselStepper from "@store/Library/Components/GreifCarousel/GreifCarouselStepper";
import { GreifCarouselSlideModel } from "@store/Library/Components/GreifCarousel/types";
import { ButtonVariant, buttonVariantOptions } from "@store/Framework/Common/Fields/Toolkit/buttonVariant";
import { getFieldDefinitionDataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";

const enum fields {
    backgroundColor = "backgroundColor",
    borderColor = "borderColor",
    stepperColor = "stepperColor",
    autoPlay = "autoPlay",
    autoPlayDelay = "autoPlayDelay",
    loop = "loop",
    slides = "slides",
}

const enum slideFields {
    id = "id",
    slideTitle = "slideTitle",
    startDate = "startDate",
    endDate = "endDate",
    cta1Label = "cta1Label",
    cta1Url = "cta1Url",
    cta1IsExternal = "cta1IsExternal",
    cta1Variant = "cta1Variant",
    cta2Label = "cta2Label",
    cta2Url = "cta2Url",
    cta2IsExternal = "cta2IsExternal",
    cta2Variant = "cta2Variant",
}

type CtaConfig = {
    label: string;
    href?: string;
    isExternal?: boolean;
    variant?: ButtonVariant;
};
const hasLinkValue = (link?: LinkFieldValue) => Boolean(link?.value);

export interface GreifCarouselWidgetPresentationProps extends ExpandStyles<GreifCarouselWidgetStyles> {}

interface GreifCarouselWidgetProps extends GreifCarouselWidgetPresentationProps, WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.borderColor]: string;
        [fields.stepperColor]: string;
        [fields.autoPlay]: boolean;
        [fields.autoPlayDelay]: number;
        [fields.loop]: boolean;
        [fields.slides]: GreifCarouselSlideModel[];
    };
}

export interface GreifCarouselWidgetStyles {
    container?: InjectableCss;
    emblaViewport?: InjectableCss;
    emblaContainer?: InjectableCss;
    emblaSlide?: InjectableCss;
    slideZoneWrapper?: InjectableCss;
    bottomRow?: InjectableCss;
    ctaArea?: InjectableCss;
    ctaButton?: ButtonPresentationProps;
    hiddenCtaButton?: ButtonPresentationProps;
}

const ctaEnterAnimation = keyframes`
    from {
        opacity: 0;
        transform: translateY(${pxToRem(4)});
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const greifCarouselWidgetStyles: GreifCarouselWidgetStyles = {
    container: {
        css: css`
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            padding: ${pxToRem(32)};
            gap: ${pxToRem(32)};
            border: ${pxToRem(1)} solid transparent;
            border-radius: ${pxToRem(24)};
            position: relative;
            overflow: hidden;

            ${breakpoint("sm", "down")} {
                padding: ${pxToRem(24)};
            }
        `,
    },
    emblaViewport: {
        css: css`
            overflow: hidden;
            width: 100%;
            clip-path: inset(0 ${pxToRem(2)} 0 0);
        `,
    },
    emblaContainer: {
        css: css`
            display: flex;
            backface-visibility: hidden;
        `,
    },
    emblaSlide: {
        css: css`
            flex: 0 0 100%;
            min-width: 0;
            display: flex;
        `,
    },
    slideZoneWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: stretch;
            flex: 1;
            min-width: 0;
            align-self: stretch;
        `,
    },
    bottomRow: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            align-self: stretch;

            ${breakpoint("sm", "down")} {
                flex-direction: column;
                align-items: stretch;
                gap: ${pxToRem(16)};
            }
        `,
    },
    ctaArea: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${pxToRem(24)};
            animation: ${ctaEnterAnimation} 220ms ease;

            ${breakpoint("sm", "down")} {
                flex-direction: column;
                align-items: stretch;
            }
        `,
    },
    ctaButton: {
        variant: "secondary",
        mergeCss: true,
        css: css`
            min-height: ${pxToRem(40)};
            font-size: ${pxToRem(12)};
            line-height: 1.2;

            ${breakpoint("sm", "down")} {
                width: min(100%, 18rem);
                margin-inline: auto;
            }
        `,
    },
    hiddenCtaButton: {
        mergeCss: true,
        css: css`
            visibility: hidden;
            pointer-events: none;
        `,
    },
};

const ctaVariantOptions = getFieldDefinitionDataOptions(buttonVariantOptions);

const GreifCarouselWidget: FC<GreifCarouselWidgetProps> = ({
    extendedStyles,
    fields: { backgroundColor, borderColor, stepperColor, autoPlay, autoPlayDelay, loop, slides },
    id,
    overridedStyles,
}) => {
    const styles = useExpandProps(greifCarouselWidgetStyles, extendedStyles, overridedStyles);
    const { isEditing } = useShellContext();
    const now = new Date();
    const filteredSlides = slides.filter(slide => {
        return !(
            (Boolean(slide.fields.startDate) && now < new Date(slide.fields.startDate)) ||
            (Boolean(slide.fields.endDate) && now > new Date(slide.fields.endDate))
        );
    });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [hasFocusWithin, setHasFocusWithin] = useState(false);
    const [autoplayRestartKey, setAutoplayRestartKey] = useState(0);
    const emblaOptions = useRef<EmblaOptionsType>(undefined);
    const autoplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [emblaRef, embla] = useEmblaCarousel({ loop });

    const autoplayEnabled = Boolean(autoPlay && !isEditing);
    const autoplayPaused = isHovering || hasFocusWithin;

    const resetAutoplayTimer = useCallback(() => {
        setAutoplayRestartKey(previous => previous + 1);
    }, []);

    const handleMouseEnter = useCallback(() => {
        setIsHovering(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false);
    }, []);

    const handleFocus = useCallback(() => {
        setHasFocusWithin(true);
    }, []);

    const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const nextFocusedElement = event.relatedTarget as Node | null;
        if (!event.currentTarget.contains(nextFocusedElement)) {
            setHasFocusWithin(false);
        }
    }, []);

    const scrollPrev = useCallback(() => {
        embla?.scrollPrev();
        resetAutoplayTimer();
    }, [embla, resetAutoplayTimer]);

    const scrollNext = useCallback(() => {
        embla?.scrollNext();
        resetAutoplayTimer();
    }, [embla, resetAutoplayTimer]);

    const scrollTo = useCallback(
        (index: number) => {
            embla?.scrollTo(index);
            resetAutoplayTimer();
        },
        [embla, resetAutoplayTimer],
    );

    const onSelect = useCallback(() => {
        if (!embla) {
            return;
        }

        setCanScrollPrev(embla.canScrollPrev());
        setCanScrollNext(embla.canScrollNext());
        setSelectedIndex(embla.selectedScrollSnap());
    }, [embla]);

    useEffect(() => {
        embla?.on("select", onSelect);

        return () => {
            embla?.off("select", onSelect);
        };
    }, [embla, onSelect]);

    useEffect(() => {
        embla?.on("reInit", onSelect);

        return () => {
            embla?.off("reInit", onSelect);
        };
    }, [embla, onSelect]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        const options: EmblaOptionsType = {
            loop,
            watchDrag: !isEditing && isTouchDevice() && filteredSlides.length > 1,
        };
        const previousOptions = emblaOptions.current;
        emblaOptions.current = { ...options };

        if (JSON.stringify(options) !== JSON.stringify(previousOptions)) {
            embla.reInit(options);
        }
    }, [embla, filteredSlides.length, isEditing, loop]);

    useEffect(() => {
        if (embla) {
            onSelect();
        }
    }, [embla, onSelect]);

    useEffect(() => {
        if (autoplayTimeoutRef.current) {
            clearTimeout(autoplayTimeoutRef.current);
            autoplayTimeoutRef.current = null;
        }

        if (!embla || !autoplayEnabled || autoplayPaused || autoPlayDelay <= 0 || filteredSlides.length <= 1) {
            return;
        }

        const delayMs = autoPlayDelay * 1000;
        const scheduleNext = () => {
            autoplayTimeoutRef.current = setTimeout(() => {
                embla.scrollNext();
                scheduleNext();
            }, delayMs);
        };

        scheduleNext();

        return () => {
            if (autoplayTimeoutRef.current) {
                clearTimeout(autoplayTimeoutRef.current);
                autoplayTimeoutRef.current = null;
            }
        };
    }, [autoPlayDelay, autoplayEnabled, autoplayPaused, autoplayRestartKey, embla, filteredSlides.length]);

    const emptyLink: LinkFieldValue = { type: "Page", value: "" };
    const activeSlide = filteredSlides[selectedIndex] ?? filteredSlides[0];
    const cta1Link = useGetLink(activeSlide?.fields?.cta1Url ?? emptyLink);
    const cta2Link = useGetLink(activeSlide?.fields?.cta2Url ?? emptyLink);

    if (!filteredSlides.length && !isEditing) {
        return null;
    }

    const hasCta1 = Boolean(activeSlide?.fields?.cta1Label?.trim()) && Boolean(cta1Link.url);
    const hasCta2 = Boolean(activeSlide?.fields?.cta2Label?.trim()) && Boolean(cta2Link.url);
    const getSlideCtaCount = (slide?: GreifCarouselSlideModel) =>
        Number(Boolean(slide?.fields?.cta1Label?.trim()) && hasLinkValue(slide?.fields?.cta1Url)) +
        Number(Boolean(slide?.fields?.cta2Label?.trim()) && hasLinkValue(slide?.fields?.cta2Url));
    const maxCtaCount = filteredSlides.reduce((currentMax, slide) => Math.max(currentMax, getSlideCtaCount(slide)), 0);
    const showFirstCtaSlot = maxCtaCount > 0;
    const showSecondCtaSlot = maxCtaCount > 1;
    const activeCtas: CtaConfig[] = [];

    if (hasCta1 && activeSlide) {
        activeCtas.push({
            label: activeSlide.fields.cta1Label,
            href: cta1Link.url || undefined,
            isExternal: activeSlide.fields.cta1IsExternal,
            variant: activeSlide.fields.cta1Variant,
        });
    }

    if (hasCta2 && activeSlide) {
        activeCtas.push({
            label: activeSlide.fields.cta2Label,
            href: cta2Link.url || undefined,
            isExternal: activeSlide.fields.cta2IsExternal,
            variant: activeSlide.fields.cta2Variant,
        });
    }

    const renderCtaSlot = (cta: CtaConfig | undefined, key: string) => {
        if (!cta) {
            return (
                <Button {...styles.ctaButton} {...styles.hiddenCtaButton} key={key} tabIndex={-1} aria-hidden={true}>
                    &nbsp;
                </Button>
            );
        }

        return (
            <Button
                {...styles.ctaButton}
                key={key}
                variant={(cta.variant || "invertedAlt") as any}
                href={cta.href}
                target={cta.isExternal ? "_blank" : undefined}
                rel={cta.isExternal ? "noopener noreferrer" : undefined}
                icon={
                    cta.isExternal
                        ? {
                              position: "right",
                              src: ExternalLink,
                          }
                        : undefined
                }
            >
                {cta.label}
            </Button>
        );
    };

    return (
        <StyledWrapper
            {...styles.container}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
            role="region"
            aria-label={translate("Carousel")}
            aria-roledescription={translate("Carousel")}
            style={{ background: backgroundColor, borderColor }}
        >
            <StyledWrapper {...styles.emblaViewport} ref={emblaRef}>
                <StyledWrapper {...styles.emblaContainer} aria-live="polite">
                    {filteredSlides.map((slide, index) => (
                        <StyledWrapper
                            {...styles.emblaSlide}
                            key={slide.fields.id || index}
                            role="group"
                            aria-roledescription={translate("slide")}
                            aria-label={translate("Slide {0} of {1}", String(index + 1), String(filteredSlides.length))}
                        >
                            <StyledWrapper {...styles.slideZoneWrapper}>
                                <Zone contentId={id} zoneName={`Slide-${slide.fields.id || index}`} />
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.bottomRow}>
                <StyledWrapper {...styles.ctaArea} key={selectedIndex}>
                    {showFirstCtaSlot && renderCtaSlot(activeCtas[0], "cta-slot-1")}
                    {showSecondCtaSlot && renderCtaSlot(activeCtas[1], "cta-slot-2")}
                </StyledWrapper>
                <GreifCarouselStepper
                    slideCount={filteredSlides.length}
                    selectedIndex={selectedIndex}
                    stepperColor={stepperColor}
                    onPrev={scrollPrev}
                    onNext={scrollNext}
                    onDotClick={scrollTo}
                    canScrollPrev={canScrollPrev || loop}
                    canScrollNext={canScrollNext || loop}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = { displayName: "Content", sortOrder: 0 };
const settingsTab = { displayName: "Settings", sortOrder: 1 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: GreifCarouselWidget,
    definition: {
        displayName: "Greif Carousel",
        group: "Basic",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.slides,
                displayName: "Slides",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Contextual",
                tab: contentTab,
                getDisplay: (item: any) => item.fields.slideTitle || "Slide",
                fieldDefinitions: [
                    {
                        name: slideFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: slideFields.slideTitle,
                        displayName: "Slide Name",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                        tooltip: "Used as the slide label in CMS.",
                    },
                    {
                        name: slideFields.startDate,
                        displayName: "Start Date",
                        editorTemplate: "DateTimeField",
                        defaultValue: null,
                    },
                    {
                        name: slideFields.endDate,
                        displayName: "End Date",
                        editorTemplate: "DateTimeField",
                        defaultValue: null,
                    },
                    {
                        name: slideFields.cta1Label,
                        displayName: "CTA 1 Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tooltip: "Leave empty to hide this CTA.",
                    },
                    {
                        name: slideFields.cta1Variant,
                        displayName: "CTA 1 Variant",
                        editorTemplate: "DropDownField",
                        options: ctaVariantOptions,
                        defaultValue: buttonVariantOptions.invertedAlt.id,
                        isVisible: (item: any) => Boolean(item.fields[slideFields.cta1Label]),
                    },
                    {
                        name: slideFields.cta1Url,
                        displayName: "CTA 1 Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: (item: any) => Boolean(item.fields[slideFields.cta1Label]),
                    },
                    {
                        name: slideFields.cta1IsExternal,
                        displayName: "CTA 1 Opens in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "When enabled, the link opens in a new tab and shows an external link icon.",
                        isVisible: (item: any) => Boolean(item.fields[slideFields.cta1Label]),
                    },
                    {
                        name: slideFields.cta2Label,
                        displayName: "CTA 2 Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tooltip: "Optional second button. Leave empty to hide. Only available after CTA 1 is set.",
                        isVisible: (item: any) => Boolean(item.fields[slideFields.cta1Label]),
                    },
                    {
                        name: slideFields.cta2Variant,
                        displayName: "CTA 2 Variant",
                        editorTemplate: "DropDownField",
                        options: ctaVariantOptions,
                        defaultValue: buttonVariantOptions.invertedOutlineAlt.id,
                        isVisible: (item: any) =>
                            Boolean(item.fields[slideFields.cta1Label]) && Boolean(item.fields[slideFields.cta2Label]),
                    },
                    {
                        name: slideFields.cta2Url,
                        displayName: "CTA 2 Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: (item: any) =>
                            Boolean(item.fields[slideFields.cta1Label]) && Boolean(item.fields[slideFields.cta2Label]),
                    },
                    {
                        name: slideFields.cta2IsExternal,
                        displayName: "CTA 2 Opens in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "When enabled, the link opens in a new tab and shows an external link icon.",
                        isVisible: (item: any) =>
                            Boolean(item.fields[slideFields.cta1Label]) && Boolean(item.fields[slideFields.cta2Label]),
                    },
                ],
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#FFFFFF1A",
                fieldType: "Contextual",
                tab: settingsTab,
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#FFFFFF33",
                fieldType: "Contextual",
                tab: settingsTab,
            },
            {
                name: fields.stepperColor,
                displayName: "Stepper Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#FFFFFF",
                fieldType: "Contextual",
                tab: settingsTab,
                tooltip: "Controls the color of the dots, active line indicator, and arrow button borders.",
            },
            {
                name: fields.autoPlay,
                displayName: "Enable Auto-Play",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: settingsTab,
            },
            {
                name: fields.autoPlayDelay,
                displayName: "Auto-Play Delay (seconds)",
                editorTemplate: "IntegerField",
                defaultValue: 5,
                min: 1,
                fieldType: "Contextual",
                tab: settingsTab,
                isVisible: (x: any) => x.fields[fields.autoPlay],
                tooltip: "Number of seconds between slide transitions. Auto-play pauses while editing.",
            },
            {
                name: fields.loop,
                displayName: "Loop Slides",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: settingsTab,
                tooltip: "When enabled, the carousel wraps around from the last slide back to the first.",
            },
        ],
    },
};

export default widgetModule;
